// src/services/studentService.js
import api from './api';
import { normalizeTeacher } from "../utils/normalizeTeacher";


/* ===============================
   HELPER FUNCTIONS
================================ */

// const buildQueryString = (params) => {
//   const query = params.toString();
//   return query ? `?${query}` : '';
// };


/* ===============================
   STUDENT PROFILE
================================ */


export const getStudentProfile = async () => {
  const response = await api.get(`/students/profile`);

  if (!response.data.success) {
    throw new Error('Failed to fetch student profile');
  }

  return response.data.profile;
};

/* ===============================
   TEACHERS
================================ */

export const getTeachers = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.subject) params.append('subject', filters.subject);
  if (filters.state) params.append('state', filters.state);
  if (filters.name) params.append('name', filters.name);
  if (filters.email) params.append('email', filters.email);

  console.log("📡 Query being sent:", params.toString());

  const response = await api.get(`/teachers?${params.toString()}`);

  if (!response.data.success) {
    throw new Error('Failed to fetch teachers');
  }

  const teachers = (response.data.teachers || []).map(normalizeTeacher);

  return teachers;
};

export const searchTeachersByEmail = async (email) => {
  if (!email || email.trim().length < 3) {
    throw new Error('Please enter at least 3 characters to search');
  }

  const response = await api.get(
    `/students/teachers/search/email?email=${encodeURIComponent(email)}`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to search teachers');
  }

  const teachers = (response.data.teachers || []).map(normalizeTeacher);

return {
  ...response.data,
  teachers
};
};

export const getTeacherProfile = async (teacherId) => {
  const response = await api.get(
    `/students/teachers/${teacherId}`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch teacher profile');
  }

  return response.data.teacher;
};

/* ===============================
   SESSION REQUESTS
================================ */

export const requestSession = async (payload) => {
  const response = await api.post(
    `/students/sessions/request`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to request session');
  }

  return response.data;
};

export const getSessionRequests = async () => {
  const response = await api.get(
    `/students/session-requests`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch session requests');
  }

  return response.data.requests || [];
};

/* ===============================
   SESSIONS
================================ */

export const getSessions = async () => {
  const response = await api.get(
    `/students/sessions`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch sessions');
  }

  return {
    scheduled: response.data.scheduled || [],
    completed: response.data.completed || []
  };
};

export const confirmSession = async (sessionId, success) => {
  return api.post(`/sessions/${sessionId}/confirm`, { success });
};

export const joinSession = async (sessionId) => {
  const response = await api.post(
    `/students/sessions/${sessionId}/join`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to join session');
  }

  return response.data;
};

export const requestInstantHelp = async (payload) => {

  const response = await api.post(
    "/students/help-now",
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to request help");
  }

  return response.data;
};

export const sendHeartbeat = async (sessionId) => {
  return api.post(`/sessions/${sessionId}/heartbeat`);
};

/* ===============================
   NOTIFICATIONS
================================ */

export const getNotifications = async () => {
  const response = await api.get(
    `/students/notifications`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch notifications');
  }

  return {
    notifications: response.data.notifications || [],
    unreadCount: response.data.unreadCount || 0
  };
};

export const markNotificationAsRead = async (notificationId) => {
  const response = await api.put(
    `/students/notifications/${notificationId}/read`
  );

  if (!response.data.success) {
    throw new Error('Failed to mark notification as read');
  }

  return response.data;
};

export const deleteSession = async (sessionId) => {

  const response = await api.delete(`/students/sessions/${sessionId}`);

  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to delete session");
  }

  return response.data;

};

export const dismissWelcomeBanner = async () => {
  const response = await api.put('/students/welcome-banner/dismiss');

  if (!response.data.success) {
    throw new Error('Failed to dismiss banner');
  }

  return response.data;
};
/* ===============================
   FAVORITES
================================ */

export const addFavorite = async (teacherId) => {
  const response = await api.post(
    `/students/favorites/${teacherId}`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to add favorite');
  }

  return response.data;
};

export const removeFavorite = async (teacherId) => {
  const response = await api.delete(
    `/students/favorites/${teacherId}`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to remove favorite');
  }

  return response.data;
};

export const getFavorites = async () => {
  const response = await api.get(
    `/students/favorites`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch favorites');
  }

  return response.data.teachers || [];
};

/* ===============================
   WALLET
================================ */

export const getWallet = async () => {
  const response = await api.get(
    `/students/wallet`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch wallet');
  }

  return response.data.wallet;
};

export const fundWallet = async (payload) => {
  const response = await api.post(
    `/students/wallet/fund`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fund wallet');
  }

  return response.data;
};

export const transferFunds = async (payload) => {
  const response = await api.post(
    `/students/wallet/transfer`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to transfer funds');
  }

  return response.data;
};

export const buyEDUCoins = async (payload) => {
  const response = await api.post(
    `/students/wallet/buy-coins`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to buy coins');
  }

  return response.data;
};

export const getRecommendedTeachers = async (studentId) => {
  try {

    const response = await api.get(`/students/${studentId}/recommended-teachers`);

    const teachers = (response.data.teachers || []).map(normalizeTeacher);

    return teachers;

  } catch (error) {

    console.error("Error fetching AI recommendations:", error);

    return [];

  }
};

export const donateEDUCoins = async (payload) => {
  const response = await api.post(
    `/students/wallet/donate`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to donate coins');
  }

  return response.data;
};

export const getTransactions = async () => {
  const response = await api.get(
    `/students/transactions`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch transactions');
  }

  return response.data;
};

/* ===============================
   BONUS
================================ */

export const awardSignupBonus = async () => {
  const response = await api.post(
    `/students/bonus/signup`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to award signup bonus');
  }

  return response.data;
};

/* ===============================
   STUDENT SEARCH
================================ */

export const searchStudents = async (email) => {
  if (!email || email.trim().length < 3) {
    throw new Error('Please enter at least 3 characters to search');
  }

  const response = await api.get(
    `/students/search/students?email=${encodeURIComponent(email)}`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to search students');
  }

  return response.data;
};

/* ===============================
   LEARNING STATS
================================ */

export const getLearningStats = async () => {
  const response = await api.get(
    `/students/learning-stats`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch learning stats');
  }

  return response.data.stats;
};

/* ===============================
   DASHBOARD
================================ */

export const getDashboardSummary = async () => {
  const response = await api.get(
    `/students/dashboard`
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch dashboard summary');
  }

  return response.data.dashboard;
};

/* ===============================
   DEFAULT EXPORT
================================ */

const studentService = {
  getStudentProfile,
  getTeachers,
  searchTeachersByEmail,
  getTeacherProfile,
  requestSession,
  getSessionRequests,
  getSessions,
  joinSession,
  getNotifications,
  markNotificationAsRead,
  getRecommendedTeachers,
  deleteSession,
  dismissWelcomeBanner,
  addFavorite,
  removeFavorite,
  getFavorites,
  getWallet,
  fundWallet,
  transferFunds,
  buyEDUCoins,
  donateEDUCoins,
  getTransactions,
  awardSignupBonus,
  searchStudents,
  getLearningStats,
  sendHeartbeat,
  getDashboardSummary
};

export default studentService;